import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Calendar, CalendarClock, Users, FileText, CreditCard,
  Settings, BarChart3, LogOut, Menu, UserCircle,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-item-dashboard" },
  { to: "/calendario", label: "Calendario", icon: Calendar, testid: "nav-item-calendario" },
  { to: "/agenda", label: "Agenda Collaboratori", icon: CalendarClock, testid: "nav-item-agenda" },
  { to: "/pazienti", label: "Anagrafica Clienti", icon: Users, testid: "nav-item-pazienti" },
  { to: "/preventivi", label: "Preventivi", icon: FileText, testid: "nav-item-preventivi" },
  { to: "/incassi", label: "Incassi", icon: CreditCard, testid: "nav-item-incassi" },
  { to: "/gestione", label: "Gestione", icon: Settings, testid: "nav-item-gestione" },
  { to: "/analytics", label: "Analisi", icon: BarChart3, testid: "nav-item-analytics" },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col bg-[#16212B] text-slate-300">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-white/5">
        <img src="/ptmed-logo-dark.jpg" alt="PT-MED" className="h-9 w-9 rounded-lg object-cover" />
        <div>
          <p className="font-heading font-bold text-white leading-none">PT-MED</p>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Gestionale</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto no-scrollbar">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-primary text-white shadow-sm"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/5 p-3">
        <button
          onClick={() => { navigate("/profilo"); onNavigate?.(); }}
          data-testid="nav-item-profilo"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/5 transition-colors"
        >
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
              <UserCircle className="h-5 w-5 text-primary" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user?.name}</p>
            <p className="truncate text-xs text-slate-500 capitalize">{user?.role}</p>
          </div>
        </button>
        <Button
          variant="ghost"
          onClick={logout}
          data-testid="logout-button"
          className="mt-1 w-full justify-start text-slate-400 hover:bg-white/5 hover:text-white"
        >
          <LogOut className="mr-3 h-[18px] w-[18px]" /> Esci
        </Button>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">
        <SidebarContent />
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 border-none">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <img src="/ptmed-logo-dark.jpg" alt="PT-MED" className="h-8 w-8 rounded-lg object-cover" />
            <span className="font-heading font-bold">PT-MED</span>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
